import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { AppContextProvider } from '../../AppLoader';
import { ThemedText } from '../../ThemedComponents';
import { VerifyCodeStore } from '../../../stores/verifyCode';
import { CodeStepCard } from '../PremiumUsers2FaEnforcement/CodeStepCard';
import { ResendVerificationCode } from '../ResendVerificationCode';
import { VerificationState } from '../VerifyButton';
import { AccountSettingsFooter, useDealerOfferView } from './DealerOfferCard';

interface VerifyCodeRedesignProps {
  store: VerifyCodeStore;
  title: string;
  infoI18Key: string;
  target?: string;
  clean: () => void;
}

/**
 * redesigned "enter the code we sent" step of the Dealer offers (wix-2025): the
 * enforcement redesign's `CodeStepCard`, driven by a post-login `VerifyCodeStore`.
 */
export const VerifyCodeRedesign: React.FC<VerifyCodeRedesignProps> = observer(
  ({ store, title, infoI18Key, target, clean }) => {
    const {
      rootStore: { navigationStore },
    } = useContext(AppContextProvider);
    useDealerOfferView();

    useEffect(() => {
      store.resendSuccessfullyIndication = true;
    }, []);

    const onVerify = async () => {
      if (store.verificationState !== VerificationState.Verifying) {
        await store.verifyCode();
      }
    };

    return (
      <CodeStepCard
        title={title}
        subtitle={
          <Trans i18nKey={infoI18Key} values={{ value: target }}>
            Verify your new account email by entering the 6-digit code we sent
            to:
            <ThemedText weight="bold" dir="ltr">
              email
            </ThemedText>
          </Trans>
        }
        onBack={() => {
          clean();
          navigationStore.goBack();
        }}
        onVerify={onVerify}
        verifyDisabled={!store.confirmCode.isValid}
        showLoader={
          store.verificationState === VerificationState.Verifying ||
          store.verificationState === VerificationState.Verified
        }
        field={store.confirmCode}
        resend={
          <ResendVerificationCode
            sendConfirmationCode={store.resendCode}
            resendSuccessfullyIndication={store.resendSuccessfullyIndication}
            resendThrottleTimerValue={store.resendThrottleTimerValue}
            resendErrorMessage={store.resendErrorMessage}
            resendButtonSize="small"
            resendIndicationTranslationKey="code.resend.indication"
            showAsButton
            variant="themed"
          />
        }
        footer={<AccountSettingsFooter />}
        priority="primary"
      />
    );
  },
);
